import { useState } from "react";
import { API } from "../api";
import { theme } from "../theme";
import { useApp } from "../AppContext";
import PortfolioChart from "../components/PortfolioChart";
import MiniChart from "../components/MiniChart";
import { positionColor, timeAgo } from "../utils";

const RANGES = [7, 30, 90];

// ── Historie: velký graf portfolia + export + přehled produktů ─────────────────
export default function HistoryPage() {
  const { token, products, loadingProducts, shopMissing } = useApp();
  const [days, setDays] = useState(30);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const exportCsv = async () => {
    setExporting(true); setExportError("");
    try {
      const res = await fetch(API + "/export/csv", { headers: { "x-license-token": token } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "rankio-export.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError("Export se nepodařil: " + e.message);
    }
    setExporting(false);
  };

  const withHistory = products.filter(p => (p.history || []).length >= 2);

  return (
    <>
      <div className="fadeUp" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.6px", color: theme.ink, marginBottom: 6 }}>Historie</h1>
          <p style={{ color: theme.inkSoft, fontSize: 14 }}>Jak se vaše pozice vyvíjejí v čase</p>
        </div>
        <button onClick={exportCsv} disabled={exporting} style={{
          background: theme.bg, border: `1px solid ${theme.line}`, borderRadius: theme.rBtn,
          padding: "10px 16px", color: theme.inkSoft, fontSize: 13.5, fontWeight: 600,
          display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: "inherit",
          opacity: exporting ? 0.7 : 1,
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>
          {exporting ? "Exportuji…" : "Export CSV"}
        </button>
      </div>

      {exportError && (
        <div style={{ background: theme.badSoft, border: `1.5px solid ${theme.badLine}`, borderRadius: theme.rCard, padding: "14px 18px", marginBottom: 20, color: theme.bad, fontSize: 14 }}>
          {exportError}
        </div>
      )}

      {/* rozsah grafu */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {RANGES.map(d => (
          <button key={d} onClick={() => setDays(d)} style={{
            padding: "7px 16px", borderRadius: 20, fontSize: 13, fontWeight: 600,
            border: "1.5px solid " + (days === d ? theme.blue : theme.line),
            background: days === d ? theme.blue : theme.bg,
            color: days === d ? "#fff" : theme.inkSoft,
            cursor: "pointer", fontVariantNumeric: theme.tnum,
          }}>{d} dní</button>
        ))}
      </div>

      <PortfolioChart token={token} days={days} height={260} shopMissing={shopMissing} key={days} />

      {/* přehled historie jednotlivých produktů */}
      <h2 style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.3px", color: theme.ink, marginBottom: 14 }}>Vývoj jednotlivých produktů</h2>
      {loadingProducts ? (
        <div style={{ textAlign: "center", padding: 40, color: theme.mut }}>Načítám…</div>
      ) : withHistory.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 24px", background: theme.bg, border: `2px dashed ${theme.line}`, borderRadius: theme.rCard, color: theme.mut, fontSize: 14 }}>
          Zatím nemáme dost kontrol na zobrazení historie produktů.
        </div>
      ) : (
        <div style={{ background: theme.bg, border: `1.5px solid ${theme.line}`, borderRadius: theme.rCard, boxShadow: theme.shadowSm, overflow: "hidden" }}>
          {withHistory.map((p, i) => {
            const hist = p.history || [];
            const last = hist[hist.length - 1]?.our_position;
            const prev = hist[hist.length - 2]?.our_position;
            const delta = (last && prev) ? prev - last : null;
            return (
              <div key={p.id} style={{
                display: "flex", alignItems: "center", gap: 14, padding: "14px 20px",
                borderTop: i === 0 ? "none" : `1px solid ${theme.line}`, flexWrap: "wrap",
              }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: theme.ink, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</p>
                  <p style={{ fontSize: 11.5, color: theme.mut }}>{timeAgo(p.last_checked)} · <span style={{ fontVariantNumeric: theme.tnum }}>{hist.length}</span> záznamů</p>
                </div>
                <MiniChart history={hist} />
                {delta !== null && (
                  <span style={{ fontVariantNumeric: theme.tnum, fontSize: 13, fontWeight: 700, color: delta > 0 ? theme.ok : delta < 0 ? theme.bad : theme.mut, minWidth: 44, textAlign: "right" }}>
                    {delta > 0 ? `↑ ${delta}` : delta < 0 ? `↓ ${Math.abs(delta)}` : "–"}
                  </span>
                )}
                <span style={{ fontVariantNumeric: theme.tnum, fontWeight: 700, fontSize: 14, padding: "5px 10px", borderRadius: 9, background: theme.bgSoft, color: positionColor(p.position), minWidth: 46, textAlign: "center" }}>
                  #{p.position || "—"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
